import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import jwt_decode from 'jwt-decode';
import { firstValueFrom } from 'rxjs';
import { UserType, UserTypeWithOrders } from '@shared/schemas/userSchema';
import { UsersService } from '../pages/prayers/services/users.service';
import {
  addUserToStore,
  setCurrentUser,
} from '../store/slices/usersSlice/usersSlice.actions';
import { AppStore } from '../store/store';

interface JwtPayload {
  _id?: string;
  email?: string;
  iat?: number;
  exp?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly tokenKey = 'auth_token';

  constructor(
    private store: Store<AppStore>,
    private usersService: UsersService,
  ) {}

  // ── Token lifecycle ────────────────────────────────────────────────

  login(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('UID'); // legacy key from older code paths
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  decodeToken(token: string): JwtPayload | null {
    try {
      return jwt_decode<JwtPayload>(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // ── Session restore on browser refresh ─────────────────────────────
  //
  // Called by APP_INITIALIZER on app startup. If a JWT is present in
  // localStorage, decode it, fetch the full user from the server, and
  // hydrate the NgRx store so guards and components see the logged-in
  // user — including their `hasAccess` rank — immediately, without a
  // re-login.
  //
  // Never rejects: on any unexpected failure the app continues to load.
  // Specific failure modes:
  //   - no token             → no-op (user is genuinely logged out)
  //   - malformed token      → clear token
  //   - 401/404 from server  → user deleted or not authorised, clear token
  //   - network / 5xx        → keep token, log warning, continue
  //
  // Previous bug: this read `decodedToken.userId` but the server signs
  // tokens as `{ _id, email }`. The field never matched, so the method
  // (which wasn't even called at app start) was a no-op even when fixed.

  async restoreSession(): Promise<void> {
    const token = this.getToken();
    if (!token) return;

    const decoded = this.decodeToken(token);
    if (!decoded?._id) {
      this.logout();
      return;
    }

    try {
      const user: UserTypeWithOrders = await firstValueFrom(
        this.usersService.getUserById(decoded._id),
      );

      if (!user || !user._id) {
        this.logout();
        return;
      }

      // Hydrate both slices: `currentUser` (who is logged in, drives
      // hasAccess checks across the app) and `allUsers` (the cache used
      // by list pages).
      this.store.dispatch(setCurrentUser({ user: user as UserType }));
      this.store.dispatch(addUserToStore({ user }));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404 || status === 401) {
        this.logout();
      } else {
        console.warn('restoreSession: transient error, keeping token', err);
      }
    }
  }
}
